import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';
import { agentRoleSchema } from './hierarchy';
import { relationTypeSchema } from './hierarchy';
import { executionModeSchema } from './workspace';
import { jsonSchemaSchema, toolConfigSchema, toolKindSchema } from './tool';
import { integrationKeySchema, integrationProviderSchema } from './integration';
import { reviewModeSchema } from './workflow-type';
import { runStageNameSchema } from './stage';
import { triggerConfigSchema, triggerTypeSchema } from './trigger';

/**
 * Name-based proposal shapes (Epic B). The AI Trainer emits agents/relations/
 * taxonomy/policies referenced by name; the apply engine resolves names to ids
 * when it transactionally materializes the proposal into a workspace.
 */
export const proposedAgentSchema = z.object({
  name: z.string().min(1).max(200),
  agentRole: agentRoleSchema.default('specialist'),
  tier: z.number().int().min(1).max(5).default(2),
  jobDescription: z.string().max(2000).default(''),
  systemPrompt: z.string().max(4000).default(''),
});
export type ProposedAgent = z.infer<typeof proposedAgentSchema>;

export const proposedRelationSchema = z.object({
  parentName: z.string().min(1),
  childName: z.string().min(1),
  relationType: relationTypeSchema.default('orchestrates'),
});
export type ProposedRelation = z.infer<typeof proposedRelationSchema>;

export const proposedTaxonomySchema = z.object({
  key: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_.-]+$/, 'key must be kebab/snake case'),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  keywords: z.array(z.string()).default([]),
  targetAgentName: z.string().optional(),
  priority: z.number().int().default(0),
});
export type ProposedTaxonomy = z.infer<typeof proposedTaxonomySchema>;

export const proposedReviewPolicySchema = z.object({
  labelKey: z.string().nullish(),
  agentName: z.string().nullish(),
  executionMode: executionModeSchema.default('assist'),
  confidenceThreshold: z.number().min(0).max(1).default(0.7),
});
export type ProposedReviewPolicy = z.infer<typeof proposedReviewPolicySchema>;

export const proposedToolSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9_.-]+$/, 'tool key must be kebab/snake case'),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  kind: toolKindSchema,
  inputSchema: jsonSchemaSchema.default({ type: 'object' }),
  outputSchema: jsonSchemaSchema.optional(),
  config: toolConfigSchema,
  requiresApproval: z.boolean().default(false),
  rateLimitPerMinute: z.number().int().min(0).default(60),
  enabled: z.boolean().default(true),
  assignedAgentNames: z.array(z.string().min(1)).default([]),
});
export type ProposedTool = z.infer<typeof proposedToolSchema>;

export const proposedSkillSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9_.-]+$/, 'skill key must be kebab/snake case'),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  instructions: z.string().max(20_000).default(''),
  examples: z.array(z.object({ input: z.string(), output: z.string() })).default([]),
  assignedAgentNames: z.array(z.string().min(1)).default([]),
});
export type ProposedSkill = z.infer<typeof proposedSkillSchema>;

export const proposedIntegrationSchema = z.object({
  provider: integrationProviderSchema,
  key: integrationKeySchema,
  name: z.string().min(1).max(200),
  config: z.record(z.unknown()).default({}),
  assignedAgentNames: z.array(z.string().min(1)).default([]),
});
export type ProposedIntegration = z.infer<typeof proposedIntegrationSchema>;

export const proposedWorkflowTypeSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_.-]+$/, 'key must be kebab/snake case'),
  name: z.string().min(1).max(200),
  executionMode: executionModeSchema.default('assist'),
  reviewMode: reviewModeSchema.default('human'),
  enabledStages: z.array(runStageNameSchema).default([]),
  reviewerRoles: z.array(z.string()).default([]),
  confidenceThreshold: z.number().min(0).max(1).default(0.7),
});
export type ProposedWorkflowType = z.infer<typeof proposedWorkflowTypeSchema>;

export const proposedTriggerSchema = z.object({
  type: triggerTypeSchema.default('webhook'),
  name: z.string().min(1).max(200),
  config: triggerConfigSchema.default({}),
  targetAgentName: z.string().optional(),
  /** Integration name whose provider will publish run outcomes back (resolved by key during apply). */
  publishIntegrationKey: z.string().optional(),
});
export type ProposedTrigger = z.infer<typeof proposedTriggerSchema>;

export const workspaceProposalSchema = z.object({
  summary: z.string().default(''),
  agents: z.array(proposedAgentSchema).default([]),
  relations: z.array(proposedRelationSchema).default([]),
  taxonomy: z.array(proposedTaxonomySchema).default([]),
  reviewPolicies: z.array(proposedReviewPolicySchema).default([]),
  tools: z.array(proposedToolSchema).default([]),
  skills: z.array(proposedSkillSchema).default([]),
  integrations: z.array(proposedIntegrationSchema).default([]),
  workflowTypes: z.array(proposedWorkflowTypeSchema).default([]),
  triggers: z.array(proposedTriggerSchema).default([]),
});
export type WorkspaceProposal = z.infer<typeof workspaceProposalSchema>;

export const proposalStatuses = ['draft', 'applied', 'discarded'] as const;
export const proposalStatusSchema = z.enum(proposalStatuses);
export type ProposalStatus = z.infer<typeof proposalStatusSchema>;

export const workspaceProposalRecordSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema,
    status: proposalStatusSchema.default('draft'),
    prompt: z.string().default(''),
    summary: z.string().default(''),
    proposal: workspaceProposalSchema,
    appliedAt: z.coerce.date().nullish(),
  })
  .merge(timestampsSchema);
export type WorkspaceProposalRecord = z.infer<typeof workspaceProposalRecordSchema>;

export const createProposalSchema = z.object({
  prompt: z.string().min(1).max(8000),
  knowledgeBaseId: idSchema.optional(),
  /** @deprecated The backend now auto-detects create vs update intent from the workspace state.
   *  The snapshot is always loaded — empty workspace → greenfield, populated → merge. */
  update: z.boolean().optional(),
});
export type CreateProposal = z.infer<typeof createProposalSchema>;

/** A point-in-time snapshot of the workspace used as context for update proposals. */
export interface WorkspaceSnapshot {
  agents: { name: string; agentRole: string; tier: number; jobDescription: string; systemPrompt: string }[];
  relations: { parentName: string; childName: string; relationType: string }[];
  taxonomy: { key: string; name: string; keywords: string[]; targetAgentName?: string }[];
  reviewPolicies: { labelKey: string | null; agentName: string | null; executionMode: string; confidenceThreshold: number }[];
  tools: { key: string; name: string; kind: string; assignedAgentNames: string[] }[];
  skills: { key: string; name: string; assignedAgentNames: string[] }[];
  integrations: { key: string; provider: string; name: string }[];
  workflowTypes: { key: string; name: string; executionMode: string; reviewMode: string }[];
  triggers: { type: string; name: string; targetAgentName?: string }[];
}

/** Counts returned after a proposal is applied to a workspace. */
export interface ApplyProposalResult {
  proposalId: string;
  agents: number;
  relations: number;
  taxonomy: number;
  reviewPolicies: number;
  tools: number;
  skills: number;
  integrations: number;
  workflowTypes: number;
  triggers: number;
  toolAssignments: number;
  skillAssignments: number;
  integrationAssignments: number;
  /** Summary of what changed (agents added/updated, etc.) — populated for update proposals. */
  changeSummary?: string;
}
